import { useEffect, useMemo, useRef, useState, type Dispatch, type DragEvent, type FormEvent, type SetStateAction } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { LookDto, LookInput, ProductDetailDto, ProductSummaryDto, ProductVariantDto } from '@ecom/contracts';
import { useLooksStore } from '@/stores/looks.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { categoryOptions } from '@/lib/options';
import { thousandthsToUnits } from '@/lib/price-input';
import {
  COLOR_OPTION,
  defaultLookVariant,
  detailLabel,
  fabricImage,
  fabricOfImage,
  fabricThumb,
  fabricsOf,
  type Fabric,
} from '@/lib/fabrics';
import { AsyncSelect } from '@/components/async-select';
import { BilingualField, type BilingualValue } from '@/components/fields';
import { CheckIcon, GripIcon, ImageIcon, PlusIcon, SearchIcon, SparkleIcon, StarIcon, TrashIcon, UploadIcon } from '@/components/icons';
import { DetailLayout, KeyValue, Page, SaveBar, Section, Sheet, Thumb } from '@/components/page';
import { SortableGrid, SortableItem } from '@/components/sortable';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  IconButton,
  Input,
  Pill,
  Select,
  Skeleton,
  Switch,
  cx,
} from '@/components/ui';

/**
 * One look, on a page of its own (R17, docs/01-Business/Looks.md).
 *
 * It used to be a dialog over the list, which is too small for what a look is:
 * photographs and the fabrics in them. Here the model photographs lead — the
 * look worn, its cover first (ADR-054) — then the things in it, each a fabric
 * chosen by its photograph and how much of it, then the words. Visibility, filing and the
 * total sit in the side column, where they are checked rather than written.
 *
 * A look has no price of its own; its total is the server's, from its lines at
 * today's prices, and is shown as of the last save (ADR-004: never summed here).
 */

const MAX_ITEMS = 12;
/** The cover and seven more (ADR-054). */
const MAX_PHOTOS = 8;

/** A photograph of the look worn, as uploaded: its storage key and where to see it. */
interface Photo {
  key: string;
  url: string | null;
}

/** The look's photographs in order: its portrait, the cover, then the rest. */
function photosOf(look: LookDto | null): Photo[] {
  if (!look) return [];
  const rest = (look.galleryKeys ?? []).map((key, index) => ({ key, url: look.galleryUrls[index] ?? null }));
  return look.portraitKey ? [{ key: look.portraitKey, url: look.portraitUrl }, ...rest] : rest;
}

/** One line of the look as the editor holds it — enough to draw it without a round trip. */
interface Row {
  variantId: string;
  quantity: number;
  productName: string;
  /** The colour, as it reads. */
  fabric: string;
  /** The rest of the variant — "10 m roll". */
  detail: string;
  sku: string;
  imageUrl: string | null;
  swatchHex: string | null;
  unitPrice: number;
  measureUnit: 'm' | 'kg' | 'l' | null;
  measureQuantity: number | null;
  inStock: boolean;
}

function rowFromItem(item: LookDto['items'][number]): Row {
  const { [COLOR_OPTION]: fabric, ...rest } = item.optionLabels;
  return {
    variantId: item.variantId,
    quantity: item.quantity,
    productName: item.productName,
    fabric: fabric ?? Object.values(rest)[0] ?? item.sku,
    detail: fabric ? Object.values(rest).join(' · ') : Object.values(rest).slice(1).join(' · '),
    sku: item.sku,
    imageUrl: item.imageUrl,
    swatchHex: item.swatchHex,
    unitPrice: item.unitPrice,
    measureUnit: item.measureUnit,
    measureQuantity: item.measureQuantity,
    inStock: item.inStock,
  };
}

function rowFromVariant(product: ProductDetailDto, fabric: Fabric, variant: ProductVariantDto, quantity: number): Row {
  return {
    variantId: variant.id,
    quantity,
    productName: product.name,
    fabric: fabric.color ? fabric.label : detailLabel(product, variant) || variant.sku,
    detail: fabric.color ? detailLabel(product, variant) : '',
    sku: variant.sku,
    imageUrl: fabricThumb(product, fabric),
    swatchHex: fabric.swatchHex,
    unitPrice: variant.price,
    measureUnit: product.measureUnit,
    measureQuantity: variant.measureQuantity,
    inStock: variant.inStock,
  };
}

export function LookEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { current, currentLoading, error, open, clearCurrent } = useLooksStore();
  const t = useT();

  const locale = useUiStore((state) => state.locale);

  // Re-read on a language change: names and option labels come resolved for the reader.
  useEffect(() => {
    if (isNew) clearCurrent();
    else void open(id);
  }, [id, isNew, open, clearCurrent, locale]);

  if (!isNew && (!current || current.id !== id)) {
    return (
      <Page title={t('editLook')} back={{ to: '/looks', label: t('looks') }} width="wide">
        {currentLoading || !error ? (
          <DetailLayout
            main={<Skeleton className="h-[28rem]" />}
            aside={<Skeleton className="h-64" />}
          />
        ) : (
          <Alert>{error}</Alert>
        )}
      </Page>
    );
  }

  // Keyed by the look, so opening another one starts its form afresh.
  return <LookForm key={isNew ? 'new' : current?.id} look={isNew ? null : current} />;
}

function LookForm({ look }: { look: LookDto | null }) {
  const { save, remove, uploadImage, saving, error } = useLooksStore();
  const locale = useUiStore((state) => state.locale);
  const navigate = useNavigate();
  const t = useT();

  const initial = useMemo(
    () => ({
      title: { en: look?.content?.title.en ?? '', ar: look?.content?.title.ar ?? '' },
      blurb: { en: look?.content?.blurb.en ?? '', ar: look?.content?.blurb.ar ?? '' },
      categoryId: look?.category?.id ?? '',
      photos: photosOf(look),
      isActive: look?.isActive ?? true,
      sortOrder: String(look?.sortOrder ?? 0),
      rows: (look?.items ?? []).map(rowFromItem),
    }),
    [look],
  );

  const [title, setTitle] = useState<BilingualValue>(initial.title);
  const [blurb, setBlurb] = useState<BilingualValue>(initial.blurb);
  const [categoryId, setCategoryId] = useState(initial.categoryId);
  const [photos, setPhotos] = useState<Photo[]>(initial.photos);
  const [isActive, setIsActive] = useState(initial.isActive);
  const [sortOrder, setSortOrder] = useState(initial.sortOrder);
  const [rows, setRows] = useState<Row[]>(initial.rows);
  const [picking, setPicking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function toInput(values: typeof initial): LookInput {
    const ar = values.title.ar.trim();
    return {
      translations: {
        // A look's photographs are all of the model (ADR-054): the detail picture
        // and its caption are cleared on every save, so an old one cannot linger
        // unseen (ADR-051).
        en: { title: values.title.en.trim(), blurb: values.blurb.en.trim() || null, detailCaption: null },
        ...(ar ? { ar: { title: ar, blurb: values.blurb.ar.trim() || null, detailCaption: null } } : {}),
      },
      categoryId: values.categoryId || null,
      portraitKey: values.photos[0]?.key ?? null,
      galleryKeys: values.photos.slice(1).map((photo) => photo.key),
      detailKey: null,
      isActive: values.isActive,
      sortOrder: /^\d{1,4}$/.test(values.sortOrder) ? Number(values.sortOrder) : 0,
      items: values.rows.map((row) => ({ variantId: row.variantId, quantity: row.quantity })),
    };
  }

  const input = toInput({ title, blurb, categoryId, photos, isActive, sortOrder, rows });
  const dirty = look === null || JSON.stringify(input) !== JSON.stringify(toInput(initial));

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    setFormError(null);
    if (!title.en.trim()) {
      setFormError(t('lookTitleRequired'));
      return;
    }
    if (rows.length === 0) {
      setFormError(t('lookNeedsItems'));
      return;
    }
    const saved = await save(look?.id ?? null, input);
    if (saved && !look) navigate(`/looks/${saved.id}`, { replace: true });
  }

  const discard = () => {
    setTitle(initial.title);
    setBlurb(initial.blurb);
    setCategoryId(initial.categoryId);
    setPhotos(initial.photos);
    setIsActive(initial.isActive);
    setSortOrder(initial.sortOrder);
    setRows(initial.rows);
    setFormError(null);
  };

  const heading = look ? look.title : t('newLook');

  return (
    <Page
      title={heading}
      back={{ to: '/looks', label: t('looks') }}
      meta={look ? <Pill tone={look.isActive ? 'success' : 'neutral'}>{look.isActive ? t('lookActive') : t('hidden')}</Pill> : null}
      actions={
        look ? (
          <Button variant="secondary" onClick={() => setDeleting(true)}>
            <TrashIcon className="size-4" />
            {t('deleteEntry')}
          </Button>
        ) : null
      }
      width="wide"
    >
      <form id="look-form" onSubmit={submit} className="contents">
        <DetailLayout
          main={
            <>
              <Section title={t('lookProducts')} subtitle={t('lookProductsHint')} flush action={
                rows.length < MAX_ITEMS ? (
                  <Button variant="secondary" size="sm" onClick={() => setPicking(true)}>
                    <PlusIcon className="size-4" />
                    {t('lookAddProducts')}
                  </Button>
                ) : (
                  <span className="text-xs text-ink-muted">{t('pickerFull')}</span>
                )
              }>
                {rows.length === 0 ? (
                  <div className="border-t border-line">
                    <EmptyState
                      title={t('lookItemsEmpty')}
                      body={t('pickerSubtitle')}
                      icon={<SparkleIcon />}
                      action={
                        <Button onClick={() => setPicking(true)}>
                          <PlusIcon className="size-4" />
                          {t('lookAddProducts')}
                        </Button>
                      }
                    />
                  </div>
                ) : (
                  <SortableGrid
                    items={rows.map((row) => ({ ...row, id: row.variantId }))}
                    className="flex flex-col border-t border-line"
                    onReorder={(next) => setRows(next.map(({ id: _id, ...row }) => row))}
                  >
                    {(row, index) => (
                      <SortableItem key={row.variantId} id={row.variantId} className="border-b border-line bg-surface last:border-0">
                        {({ attributes, listeners }) => (
                          <ItemRow
                            row={row}
                            handle={{ ...attributes, ...listeners }}
                            onQuantity={(quantity) =>
                              setRows((current) => current.map((entry, at) => (at === index ? { ...entry, quantity } : entry)))
                            }
                            onRemove={() => setRows((current) => current.filter((_, at) => at !== index))}
                          />
                        )}
                      </SortableItem>
                    )}
                  </SortableGrid>
                )}
              </Section>

              <Section title={t('lookContent')}>
                <div className="flex flex-col gap-4">
                  <BilingualField label={t('lookTitle')} value={title} onChange={setTitle} />
                  <BilingualField label={t('lookBlurb')} value={blurb} onChange={setBlurb} multiline rows={3} />
                </div>
              </Section>
            </>
          }
          aside={
            <>
              <Section title={t('lookModelPhoto')} subtitle={t('lookModelPhotoHint')}>
                <ModelPhotos photos={photos} setPhotos={setPhotos} upload={uploadImage} />
              </Section>

              <Section title={t('lookVisibility')}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-ink">{t('lookActive')}</p>
                    <p className="mt-0.5 text-xs text-ink-muted">{t('lookActiveHint')}</p>
                  </div>
                  <Switch label={t('lookActive')} checked={isActive} onChange={setIsActive} />
                </div>
              </Section>

              <Section title={t('lookOrganization')}>
                <div className="flex flex-col gap-4">
                  <Field label={t('category')} htmlFor="look-category">
                    <AsyncSelect
                      id="look-category"
                      value={categoryId}
                      onChange={(value) => setCategoryId(value)}
                      fetchPage={categoryOptions.page}
                      fetchOne={categoryOptions.one}
                      emptyLabel={t('noCategory')}
                    />
                  </Field>
                  <Field label={t('lookOrder')} htmlFor="look-order" hint={t('lookOrderHint')}>
                    <Input
                      id="look-order"
                      dir="ltr"
                      inputMode="numeric"
                      value={sortOrder}
                      onChange={(event) => setSortOrder(event.target.value.replace(/[^0-9]/g, ''))}
                    />
                  </Field>
                </div>
              </Section>

              <Section title={t('lookSummary')}>
                <dl className="flex flex-col gap-3">
                  <KeyValue label={t('lookItems')}>{t('lookPieces', { count: count(rows.length, locale) })}</KeyValue>
                  <KeyValue label={t('lookTotal')}>
                    {look ? (
                      <span data-numeric className="text-base font-semibold">
                        {money(look.total, locale, look.currency)}
                      </span>
                    ) : (
                      '—'
                    )}
                    <span className="mt-0.5 block text-xs text-ink-muted">
                      {look && !dirty ? t('lookTotalHint') : t('lookTotalAfterSave')}
                    </span>
                  </KeyValue>
                </dl>
                {rows.length > 0 ? (
                  <div className="mt-4 flex -space-x-2 rtl:space-x-reverse" aria-hidden>
                    {rows.slice(0, 8).map((row) => (
                      <Thumb
                        key={row.variantId}
                        src={mediaUrl(row.imageUrl)}
                        swatch={row.swatchHex}
                        size="sm"
                        className="rounded-full border-2 border-surface"
                      />
                    ))}
                  </div>
                ) : null}
              </Section>
            </>
          }
        />
      </form>

      <SaveBar
        saving={saving}
        dirty={dirty}
        form="look-form"
        message={formError || error ? <Alert>{formError ?? error}</Alert> : undefined}
        onDiscard={look && dirty ? discard : undefined}
      />

      <FabricPicker
        open={picking}
        taken={new Set(rows.map((row) => row.variantId))}
        room={MAX_ITEMS - rows.length}
        onClose={() => setPicking(false)}
        onAdd={(added) => {
          setRows((current) => [...current, ...added].slice(0, MAX_ITEMS));
          setPicking(false);
        }}
      />

      <ConfirmDialog
        open={deleting}
        title={t('deleteLookTitle')}
        body={t('deleteLookBody')}
        confirmLabel={t('deleteEntry')}
        cancelLabel={t('cancel')}
        busy={saving}
        onCancel={() => setDeleting(false)}
        onConfirm={async () => {
          if (look && (await remove(look.id))) navigate('/looks', { replace: true });
        }}
      />
    </Page>
  );
}

/** One thing in the look: its fabric's picture, what it is, and how much. */
function ItemRow({
  row,
  handle,
  onQuantity,
  onRemove,
}: {
  row: Row;
  handle: Record<string, unknown>;
  onQuantity: (quantity: number) => void;
  onRemove: () => void;
}) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const unit = row.measureUnit ? t(`unit_${row.measureUnit}`) : null;

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 sm:flex-nowrap">
      <span
        {...handle}
        aria-label={t('dragToReorder')}
        className="inline-flex size-7 shrink-0 cursor-grab items-center justify-center rounded-control text-ink-faint hover:bg-surface-sunken active:cursor-grabbing"
      >
        <GripIcon className="size-4" />
      </span>
      <Thumb src={mediaUrl(row.imageUrl)} swatch={row.swatchHex} size="lg" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">{row.productName}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-ink-muted">
          {row.swatchHex ? <span className="inline-block size-2.5 rounded-full border border-line" style={{ backgroundColor: row.swatchHex }} /> : null}
          <span>{row.fabric}</span>
          {row.detail ? <span>· {row.detail}</span> : null}
          <span dir="ltr" className="text-ink-faint">
            {row.sku}
          </span>
        </p>
        <p data-numeric className="mt-0.5 text-xs text-ink-muted">
          {t('eachPrice', { price: money(row.unitPrice, locale) })}
          {!row.inStock ? <span className="ms-2 text-danger">{t('outOfStock')}</span> : null}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-end">
          <Input
            aria-label={t('quantity')}
            dir="ltr"
            inputMode="numeric"
            value={String(row.quantity)}
            onChange={(event) => {
              const amount = Number(event.target.value.replace(/[^0-9]/g, '')) || 1;
              onQuantity(Math.min(999, amount));
            }}
            className="w-20 text-center"
          />
          {unit && row.measureQuantity ? (
            <span data-numeric dir="ltr" className="mt-0.5 text-[11px] text-ink-faint">
              {t('lookAmount', { amount: thousandthsToUnits(row.quantity * row.measureQuantity), unit })}
            </span>
          ) : null}
        </div>
        <IconButton label={t('remove')} onClick={onRemove} className="size-9 border-0 text-ink-faint hover:text-danger">
          <TrashIcon className="size-4" />
        </IconButton>
      </div>
    </div>
  );
}

/**
 * The look's photographs of the model (ADR-054): the cover leads every card,
 * and all of them, in this order, show in the look's preview. Several are
 * dropped or chosen at once and uploaded one after another, as a product's
 * media is, each joining the list as it lands; the rest are dragged into order
 * or made the cover.
 */
function ModelPhotos({
  photos,
  setPhotos,
  upload,
}: {
  photos: Photo[];
  setPhotos: Dispatch<SetStateAction<Photo[]>>;
  upload: (file: File) => Promise<{ key: string; url: string } | null>;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const adding = useRef<HTMLInputElement>(null);
  const replacing = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<{ done: number; total: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const [over, setOver] = useState(false);
  const [cover, ...rest] = photos;
  const room = MAX_PHOTOS - photos.length;

  async function add(files: File[], replaceCover = false) {
    const pictures = files.filter((file) => file.type.startsWith('image/')).slice(0, replaceCover ? 1 : room);
    if (pictures.length === 0) return;
    setFailed(false);
    setBusy({ done: 0, total: pictures.length });
    for (const [index, file] of pictures.entries()) {
      const uploaded = await upload(file);
      if (!uploaded) setFailed(true);
      else if (replaceCover) setPhotos((current) => [uploaded, ...current.slice(1)]);
      else setPhotos((current) => (current.some((photo) => photo.key === uploaded.key) ? current : [...current, uploaded].slice(0, MAX_PHOTOS)));
      setBusy({ done: index + 1, total: pictures.length });
    }
    setBusy(null);
  }

  const chooser = (ref: typeof adding, replaceCover: boolean) => (
    <input
      ref={ref}
      type="file"
      accept="image/jpeg,image/png,image/webp"
      multiple={!replaceCover}
      className="sr-only"
      onChange={(event) => {
        void add([...(event.target.files ?? [])], replaceCover);
        event.target.value = '';
      }}
    />
  );

  const drop = {
    onDragOver: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes('Files') || room === 0) return;
      event.preventDefault();
      setOver(true);
    },
    onDragLeave: () => setOver(false),
    onDrop: (event: DragEvent) => {
      if (!event.dataTransfer.files.length) return;
      event.preventDefault();
      setOver(false);
      void add([...event.dataTransfer.files]);
    },
  };
  const progress = busy ? `${count(busy.done, locale)}/${count(busy.total, locale)}` : null;

  return (
    <div {...drop} className={cx('flex flex-col gap-3 rounded-control transition-colors', over && cover && 'bg-brand-50 ring-2 ring-brand-300')}>
      {chooser(adding, false)}
      {chooser(replacing, true)}
      {cover ? (
        <div className="group relative overflow-hidden rounded-control border border-brand-600 bg-surface-sunken">
          <img src={mediaUrl(cover.url)} alt="" className="aspect-[3/4] w-full object-cover" />
          <span className="absolute start-2 top-2 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-medium text-brand-ink">
            {t('lookCover')}
          </span>
          <div className="absolute inset-x-0 bottom-0 flex justify-end gap-2 bg-gradient-to-t from-black/50 to-transparent p-3">
            <Button size="sm" variant="secondary" disabled={busy !== null} onClick={() => replacing.current?.click()}>
              <UploadIcon className="size-3.5" />
              {t('replacePhoto')}
            </Button>
            <Button size="sm" variant="secondary" disabled={busy !== null} onClick={() => setPhotos((current) => current.slice(1))}>
              <TrashIcon className="size-3.5" />
              {t('remove')}
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => adding.current?.click()}
          className={cx(
            'flex aspect-[3/4] w-full flex-col items-center justify-center gap-2 rounded-control border-2 border-dashed px-4 text-center transition-colors',
            over ? 'border-brand-500 bg-brand-50' : 'border-line bg-surface-sunken hover:border-brand-300',
          )}
        >
          <span className="inline-flex size-11 items-center justify-center rounded-full bg-surface text-ink-muted shadow-card">
            <ImageIcon className="size-5" />
          </span>
          <span className="text-sm font-medium text-ink">{progress ? `${t('loading')} ${progress}` : t('lookDropPhoto')}</span>
          <span className="text-xs text-ink-muted">{t('lookPhotoFormats')}</span>
        </button>
      )}

      {rest.length > 0 ? (
        <SortableGrid
          items={rest.map((photo) => ({ ...photo, id: photo.key }))}
          disabled={busy !== null}
          className="grid grid-cols-4 gap-2"
          onReorder={(next) => setPhotos((current) => [current[0], ...next.map(({ id: _id, ...photo }) => photo)])}
        >
          {(photo, index) => (
            <SortableItem key={photo.key} id={photo.key}>
              {({ attributes, listeners }) => (
                <div className="group relative aspect-[3/4] overflow-hidden rounded-control border border-line bg-surface-sunken">
                  <img src={mediaUrl(photo.url)} alt="" className="size-full object-cover" />
                  <span
                    {...attributes}
                    {...listeners}
                    aria-label={t('dragToReorder')}
                    className="absolute inset-0 cursor-grab active:cursor-grabbing"
                  />
                  <div className="absolute inset-x-1 bottom-1 flex justify-between opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                    <button
                      type="button"
                      aria-label={t('lookMakeCover')}
                      title={t('lookMakeCover')}
                      onClick={() =>
                        setPhotos((current) => [current[index + 1], ...current.filter((_, at) => at !== index + 1)])
                      }
                      className="inline-flex size-6 items-center justify-center rounded-control bg-surface/90 text-ink-muted shadow-card hover:text-brand-600"
                    >
                      <StarIcon className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={t('remove')}
                      title={t('remove')}
                      onClick={() => setPhotos((current) => current.filter((_, at) => at !== index + 1))}
                      className="inline-flex size-6 items-center justify-center rounded-control bg-surface/90 text-ink-muted shadow-card hover:text-danger"
                    >
                      <TrashIcon className="size-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </SortableItem>
          )}
        </SortableGrid>
      ) : null}

      {cover ? (
        room > 0 ? (
          <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => adding.current?.click()} className="w-full">
            <PlusIcon className="size-4" />
            {progress ? `${t('loading')} ${progress}` : t('lookAddPhotos')}
            <span data-numeric className="ms-auto text-xs text-ink-muted">
              {t('lookPhotosCount', { count: count(photos.length, locale), max: count(MAX_PHOTOS, locale) })}
            </span>
          </Button>
        ) : (
          <p className="text-center text-xs text-ink-muted">{t('lookPhotosFull')}</p>
        )
      ) : null}
      {failed ? <Alert>{t('imageUploadFailed')}</Alert> : null}
    </div>
  );
}

// ─── The fabric picker ───────────────────────────────────────────────────────

interface Choice {
  product: ProductDetailDto;
  fabric: Fabric;
  variantId: string;
  quantity: number;
}

/**
 * Products into the look, fabric by fabric.
 *
 * A product on the start side, searched on the server a page at a time; its
 * fabrics on the other, recognised by their photographs — a colour's own
 * picture when the product links one, its swatch when not. Picks are kept
 * across products, so a look of three cloths from three bolts is one visit
 * to this panel, not three.
 */
function FabricPicker({
  open,
  taken,
  room,
  onClose,
  onAdd,
}: {
  open: boolean;
  /** Variants already in the look — a look holds each once. */
  taken: Set<string>;
  /** How many more lines the look can hold. */
  room: number;
  onClose: () => void;
  onAdd: (rows: Row[]) => void;
}) {
  const { searchProducts, loadProduct } = useLooksStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [query, setQuery] = useState('');
  const [products, setProducts] = useState<ProductSummaryDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState<ProductDetailDto | null>(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [choices, setChoices] = useState<Choice[]>([]);

  // Searched on the server, debounced — never a whole catalogue in memory.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      void searchProducts(query).then((page) => {
        if (cancelled) return;
        setProducts(page.items);
        setCursor(page.nextCursor);
        setLoading(false);
      });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, query, searchProducts]);

  // A fresh panel each time it opens.
  useEffect(() => {
    if (open) return;
    setChoices([]);
    setActive(null);
    setQuery('');
  }, [open]);

  async function choose(productId: string) {
    if (active?.id === productId) return;
    setActiveLoading(true);
    const product = await loadProduct(productId);
    setActive(product);
    setActiveLoading(false);
  }

  const chosen = (product: ProductDetailDto, fabric: Fabric) =>
    choices.find((choice) => choice.product.id === product.id && choice.fabric.key === fabric.key);

  function toggle(product: ProductDetailDto, fabric: Fabric) {
    const existing = chosen(product, fabric);
    if (existing) {
      setChoices((current) => current.filter((choice) => choice !== existing));
      return;
    }
    if (choices.length >= room) return;
    const free = fabric.variants.filter((variant) => !variant.isSample && !taken.has(variant.id));
    const preferred = defaultLookVariant(fabric);
    const variant = preferred && !taken.has(preferred.id) ? preferred : free[0];
    if (!variant) return;
    setChoices((current) => [...current, { product, fabric, variantId: variant.id, quantity: 1 }]);
  }

  const update = (target: Choice, patch: Partial<Choice>) =>
    setChoices((current) => current.map((choice) => (choice === target ? { ...choice, ...patch } : choice)));

  const fabrics = active ? fabricsOf(active) : [];

  return (
    <Sheet
      open={open}
      size="xl"
      title={t('pickerTitle')}
      subtitle={t('pickerSubtitle')}
      onClose={onClose}
      footer={
        <>
          <span className="me-auto text-sm text-ink-muted">
            {choices.length > 0 ? t('pickerSelected', { count: count(choices.length, locale) }) : null}
            {choices.length >= room ? <span className="ms-2 text-warning">{t('pickerFull')}</span> : null}
          </span>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            disabled={choices.length === 0}
            onClick={() =>
              onAdd(
                choices.flatMap((choice) => {
                  const variant = choice.fabric.variants.find((entry) => entry.id === choice.variantId);
                  return variant ? [rowFromVariant(choice.product, choice.fabric, variant, choice.quantity)] : [];
                }),
              )
            }
          >
            <PlusIcon className="size-4" />
            {t('pickerAdd')}
          </Button>
        </>
      }
    >
      <div className="grid gap-5 md:grid-cols-[17rem_minmax(0,1fr)]">
        {/* Products */}
        <div className="flex min-w-0 flex-col gap-3">
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
              <SearchIcon className="size-4" />
            </span>
            <Input
              type="search"
              value={query}
              placeholder={t('pickerSearch')}
              aria-label={t('pickerSearch')}
              onChange={(event) => setQuery(event.target.value)}
              className="ps-9"
            />
          </div>
          <ul className="flex flex-col gap-1">
            {loading && products.length === 0
              ? Array.from({ length: 6 }, (_, index) => (
                  <li key={index}>
                    <Skeleton className="h-14" />
                  </li>
                ))
              : products.map((product) => {
                  const picked = choices.filter((choice) => choice.product.id === product.id).length;
                  return (
                    <li key={product.id}>
                      <button
                        type="button"
                        onClick={() => void choose(product.id)}
                        aria-current={active?.id === product.id}
                        className={cx(
                          'flex w-full items-center gap-3 rounded-control border p-2 text-start transition-colors',
                          active?.id === product.id
                            ? 'border-brand-600 bg-brand-50'
                            : 'border-transparent hover:border-line hover:bg-surface-sunken',
                        )}
                      >
                        <Thumb src={mediaUrl(product.imageUrl)} size="md" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-ink">{product.name}</span>
                          <span className="mt-1 flex items-center gap-1">
                            {product.swatches.slice(0, 6).map((swatch) => (
                              <span
                                key={swatch.value}
                                title={swatch.label}
                                className="size-3 rounded-full border border-line"
                                style={{ backgroundColor: swatch.hex }}
                              />
                            ))}
                            {product.swatches.length > 6 ? (
                              <span className="text-[10px] text-ink-faint">+{product.swatches.length - 6}</span>
                            ) : null}
                          </span>
                        </span>
                        {picked > 0 ? (
                          <span data-numeric className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-brand-600 text-[11px] text-brand-ink">
                            {picked}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
            {!loading && products.length === 0 ? (
              <li className="px-2 py-6 text-center text-sm text-ink-muted">{t('pickerNoProducts')}</li>
            ) : null}
          </ul>
          {cursor ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                const page = await searchProducts(query, cursor);
                setProducts((current) => [...current, ...page.items]);
                setCursor(page.nextCursor);
                setLoading(false);
              }}
            >
              {loading ? t('loading') : t('loadMore')}
            </Button>
          ) : null}
        </div>

        {/* Its fabrics */}
        <div className="min-w-0">
          {activeLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="aspect-square" />
              ))}
            </div>
          ) : !active ? (
            <Card className="flex min-h-80 flex-col">
              <EmptyState title={t('pickerChooseProduct')} icon={<SparkleIcon />} />
            </Card>
          ) : (
            <div className="flex flex-col gap-6">
              <div>
                <h3 className="text-base font-semibold text-ink">{active.name}</h3>
                <p className="mt-0.5 text-sm text-ink-muted">{t('pickerChooseFabricHint')}</p>
              </div>

              <section>
                <h4 className="mb-2 text-sm font-medium text-ink">{t('pickerChooseFabric')}</h4>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {fabrics.map((fabric) => {
                    const selected = Boolean(chosen(active, fabric));
                    const sellable = fabric.variants.filter((variant) => !variant.isSample);
                    const allTaken = sellable.length > 0 && sellable.every((variant) => taken.has(variant.id));
                    const disabled = sellable.length === 0 || allTaken || (!selected && choices.length >= room);
                    const image = fabricImage(active, fabric);
                    const own = fabric.images.length > 0;
                    return (
                      <button
                        key={fabric.key}
                        type="button"
                        disabled={disabled}
                        aria-pressed={selected}
                        onClick={() => toggle(active, fabric)}
                        title={sellable.length === 0 ? t('pickerOnlySamples') : allTaken ? t('pickerInLook') : fabric.label}
                        className={cx(
                          'group relative flex flex-col overflow-hidden rounded-control border-2 bg-surface text-start transition-all disabled:cursor-not-allowed disabled:opacity-50',
                          selected ? 'border-brand-600 shadow-raised' : 'border-line hover:border-brand-300',
                        )}
                      >
                        <span className="relative block aspect-square w-full bg-surface-sunken">
                          {own || !fabric.swatchHex ? (
                            image ? (
                              <img src={mediaUrl(image)} alt="" loading="lazy" className="size-full object-cover" />
                            ) : null
                          ) : (
                            // No photograph of its own: the colour itself, over the bolt's photo.
                            <span className="absolute inset-0" style={{ backgroundColor: fabric.swatchHex }} />
                          )}
                          {fabric.swatchHex && own ? (
                            <span
                              className="absolute start-2 bottom-2 size-5 rounded-full border-2 border-white shadow-card"
                              style={{ backgroundColor: fabric.swatchHex }}
                            />
                          ) : null}
                          {selected ? (
                            <span className="absolute end-2 top-2 inline-flex size-6 items-center justify-center rounded-full bg-brand-600 text-brand-ink shadow-card">
                              <CheckIcon className="size-4" />
                            </span>
                          ) : allTaken ? (
                            <span className="absolute end-2 top-2">
                              <Pill>{t('pickerInLook')}</Pill>
                            </span>
                          ) : null}
                        </span>
                        <span className="block truncate px-2.5 py-2 text-sm font-medium text-ink">{fabric.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              {choices.some((choice) => choice.product.id === active.id) ? (
                <section className="flex flex-col gap-2">
                  {choices
                    .filter((choice) => choice.product.id === active.id)
                    .map((choice) => {
                      const sellable = choice.fabric.variants.filter((variant) => !variant.isSample);
                      return (
                        <div key={choice.fabric.key} className="flex flex-wrap items-end gap-3 rounded-control border border-line bg-surface-sunken/60 p-3">
                          <Thumb src={mediaUrl(fabricThumb(active, choice.fabric))} swatch={choice.fabric.swatchHex} size="md" />
                          <p className="min-w-24 flex-1 self-center text-sm font-medium text-ink">{choice.fabric.label}</p>
                          {sellable.length > 1 ? (
                            <Field label={t('pickerSoldAs')} htmlFor={`sold-as-${choice.fabric.key}`}>
                              <Select
                                id={`sold-as-${choice.fabric.key}`}
                                value={choice.variantId}
                                onChange={(event) => update(choice, { variantId: event.target.value })}
                              >
                                {sellable.map((variant) => (
                                  <option key={variant.id} value={variant.id} disabled={taken.has(variant.id)}>
                                    {detailLabel(active, variant) || variant.sku} — {money(variant.price, locale, active.currency)}
                                  </option>
                                ))}
                              </Select>
                            </Field>
                          ) : null}
                          <Field label={t('quantity')} htmlFor={`qty-${choice.fabric.key}`}>
                            <Input
                              id={`qty-${choice.fabric.key}`}
                              dir="ltr"
                              inputMode="numeric"
                              value={String(choice.quantity)}
                              onChange={(event) =>
                                update(choice, { quantity: Math.min(999, Number(event.target.value.replace(/[^0-9]/g, '')) || 1) })
                              }
                              className="w-20 text-center"
                            />
                          </Field>
                        </div>
                      );
                    })}
                </section>
              ) : null}

              {active.images.length > 0 ? (
                <section>
                  <h4 className="text-sm font-medium text-ink">{t('pickerPhotos')}</h4>
                  <p className="mt-0.5 mb-2 text-xs text-ink-muted">{t('pickerPhotosHint')}</p>
                  <div className="flex flex-wrap gap-2">
                    {[...active.images]
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((image) => {
                        const fabric = fabricOfImage(fabrics, image);
                        const selected = fabric ? Boolean(chosen(active, fabric)) : false;
                        return (
                          <button
                            key={image.id}
                            type="button"
                            disabled={!fabric}
                            onClick={() => fabric && toggle(active, fabric)}
                            title={fabric ? `${t('fabric')}: ${fabric.label}` : t('pickerPhotoUnlinked')}
                            className={cx(
                              'relative size-20 overflow-hidden rounded-control border-2 transition-colors disabled:cursor-default',
                              selected ? 'border-brand-600' : 'border-line',
                              fabric && !selected && 'hover:border-brand-300',
                            )}
                          >
                            <img src={mediaUrl(image.url)} alt={image.alt ?? ''} loading="lazy" className={cx('size-full object-cover', !fabric && 'opacity-60')} />
                            {fabric ? (
                              <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1 py-0.5 text-[10px] text-white">
                                {fabric.label}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                  </div>
                </section>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </Sheet>
  );
}
